/**
 * «Gjej kandidatë ekzistues» — kush ka folur tashmë me kompaninë.
 *
 * Krahasimi është i përcaktuar dhe i shpjegueshëm: kriteret e pozitës kundrejt
 * asaj që personi deklaroi kur aplikoi. Çdo rresht tregon ARSYET, jo një
 * përqindje — «4/5 kritere përputhen, gjermanishtja nuk dihet» është pohim që
 * një njeri mund ta kundërshtojë, kurse «83%» nuk është.
 *
 * Renditja nuk është renditje e kandidatëve më të mirë. Është vetëm rendi në të
 * cilin ia vlen të lexohen; vendimi mbetet i njeriut.
 *
 * Veprimi i parë është FTESË, kurrë ofertë: të dhënat mund të jenë vjetruar, dhe
 * askush nuk i premton punë dikujt bazuar në një formular të vitit të kaluar.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import recruitmentHistoryService from '../../services/recruitmentHistoryService';
import { verdictMeta, matchSummaryText } from '../../config/recruitmentCriteria';
import { statusLabel } from '../../config/recruitmentStatuses';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

function ageText(d) {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (isNaN(days)) return '';
  if (days < 31) return `${days} ditë më parë`;
  const m = Math.floor(days / 30);
  return m < 24 ? `${m} muaj më parë` : `${Math.floor(m / 12)} vjet më parë`;
}

export default function ExistingCandidatesDialog({ posting, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    if (!posting?.id) return;
    setLoading(true); setError(''); setFlash('');
    const d = await recruitmentHistoryService().matches(posting.id);
    if (d.error) setError(d.error); else setData(d);
    setLoading(false);
  };

  useEffect(() => { if (open) { setNote(''); load(); } else setData(null); /* eslint-disable-next-line */ }, [open, posting?.id]);

  const invite = async (candidateId) => {
    setBusy(candidateId); setError(''); setFlash('');
    const r = await recruitmentHistoryService().invite(candidateId, posting.id, note);
    setBusy(null);
    if (r?.error) { setError(r.error); return; }
    setFlash('Ftesa u regjistrua. Aplikimi i ri krijohet vetëm pasi personi shpreh interes.');
    load();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Kandidatë ekzistues
        <Typography variant="body2" color="text.secondary">{posting?.title || ''}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /></Stack>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {flash && <Alert severity="success" onClose={() => setFlash('')}>{flash}</Alert>}

            {data && !data.hasCriteria && (
              <Alert severity="info">
                Kjo pozitë s'ka kritere të përcaktuara, ndaj lista tregon këdo që ka aplikuar më parë,
                pa krahasim. Shto kriteret te pozita që rezultatet të kenë arsye.
              </Alert>
            )}

            <TextField size="small" label="Shënim për ftesën (opsional)" value={note}
              onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={1} />

            {data && data.data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Asnjë kandidat i mëparshëm jashtë kësaj pozite. Njerëzit që kanë aplikuar tashmë këtu
                janë te lista e kandidatëve.
              </Typography>
            ) : (
              (data?.data || []).map((c) => (
                <Box key={c.candidateId} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 700 }}>{c.name}</Typography>
                        {c.everHired && <Chip size="small" color="success" variant="outlined" label="ish-punonjës" sx={{ height: 19, fontSize: '.64rem' }} />}
                        {c.needsReview && <Chip size="small" color="warning" variant="outlined" label="identitet për shqyrtim" sx={{ height: 19, fontSize: '.64rem' }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {[c.email, c.phone].filter(Boolean).join(' · ') || 'pa kontakt'}
                      </Typography>
                      {/* Vjetërsia shfaqet gjithmonë — kjo është arsyeja pse ftohet e nuk i dërgohet ofertë. */}
                      <Typography variant="body2" sx={{ fontSize: 12.5, mt: 0.5 }}>
                        Aplikimi i fundit: <strong>{fmtDate(c.lastAppliedAt)}</strong>{' '}
                        <Typography component="span" variant="caption" color="text.secondary">{ageText(c.lastAppliedAt)}</Typography>
                        {' · '}{c.lastPosition || 'pa pozitë'} · {statusLabel(c.lastStatus)}
                        {c.applicationCount > 1 ? ` · ${c.applicationCount} aplikime` : ''}
                      </Typography>

                      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, fontWeight: 700 }}>
                        {matchSummaryText(c.summary)}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {c.criteria.map((row, i) => {
                          const m = verdictMeta(row.verdict);
                          return (
                            <Tooltip key={i} title={`${row.weight === 'required' ? 'E detyrueshme' : 'E preferuar'} · ${row.detail}`}>
                              <Chip size="small" variant={row.weight === 'required' ? 'filled' : 'outlined'}
                                color={m.color} label={`${m.mark} ${row.label}`}
                                sx={{ height: 20, fontSize: '.64rem' }} />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </Box>

                    <Stack spacing={0.5} sx={{ minWidth: 170 }}>
                      {c.invitation ? (
                        <Chip size="small" color="info" label={c.invitation.status === 'converted' ? 'U bë aplikim' : 'Ftuar tashmë'} />
                      ) : c.eligibility.eligible ? (
                        <Button size="small" variant="contained" disabled={busy === c.candidateId}
                          onClick={() => invite(c.candidateId)} sx={{ textTransform: 'none' }}>
                          Fto për këtë pozitë
                        </Button>
                      ) : (
                        <Tooltip title={c.eligibility.reason}>
                          <span>
                            <Button size="small" variant="outlined" disabled fullWidth sx={{ textTransform: 'none' }}>
                              Ftesa s'lejohet
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {!c.eligibility.eligible && !c.invitation && (
                        <Typography variant="caption" color="text.secondary">{c.eligibility.reason}</Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              ))
            )}

            <Divider />
            <Typography variant="caption" color="text.secondary">
              Ftesa nuk dërgon ofertë dhe nuk krijon aplikim. Aplikimi i ri krijohet vetëm pasi personi
              shpreh interes — dhe aplikimi i vjetër mbetet i palëvizur te pozita e vet.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Mbyll</Button>
      </DialogActions>
    </Dialog>
  );
}
